import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { EntityDetail } from "#shared/schemas/entityDetail.ts";
import type { FieldDef } from "#shared/schemas/fieldDef.ts";
import { Button } from "@web/components/ui/button.tsx";
import { cn } from "@web/lib/utils.ts";
import {
  EXPORT_FORMATS,
  buildExportRows,
  exportFilename,
  serializeExport,
  type ExportFormat,
} from "./exportDetail.ts";

const FORMAT_LABEL: Record<ExportFormat, string> = {
  json: "JSON",
  csv: "CSV",
  text: "Text",
};

/** Exports are rendered from the same rows the panel shows — never a second query. */
export function ExportSection({
  detail,
  defs,
}: {
  detail: EntityDetail;
  defs: readonly FieldDef[];
}) {
  const [format, setFormat] = useState<ExportFormat>("json");

  const payload = useMemo(
    () => serializeExport(detail, buildExportRows(detail, defs), format),
    [detail, defs, format],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(payload);
      toast.success(`${FORMAT_LABEL[format]} copied`);
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  }

  function download() {
    const url = URL.createObjectURL(new Blob([payload], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = exportFilename(detail, format);
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="space-y-3">
      <div role="group" aria-label="Export format" className="flex gap-1">
        {EXPORT_FORMATS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={format === option}
            className={cn(
              "rounded-md px-2 py-1 text-xs font-medium",
              format === option
                ? "bg-row-selected text-text"
                : "text-muted hover:bg-row-hover hover:text-text",
            )}
            onClick={() => setFormat(option)}
          >
            {FORMAT_LABEL[option]}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => void copy()}>
          Copy
        </Button>
        <Button variant="ghost" size="sm" onClick={download}>
          Download
        </Button>
      </div>

      <pre className="max-h-80 overflow-auto rounded-md border border-line bg-panel p-3 text-xs text-muted">
        {payload}
      </pre>
    </section>
  );
}
